import { StaticQuery, graphql } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Helmet from 'react-helmet';

import { isBrowser } from '../../utils';

const Meta = ({
  data,
  pageDescription = '',
  pageImage,
  pageTitle = '',
  pathname = '/',
  titleType = 'append',
}) => {
  const {
    defaultDescription,
    defaultLogo,
    defaultTitle,
    facebook,
    ogLanguage,
    siteLanguage,
    siteUrl,
    twitter,
  } = data.site.siteMetadata;

  useEffect(() => {
    if (isBrowser) {
      const viewport = document.querySelector('meta[name="viewport"]');

      if (viewport) {
        viewport.remove();
      }
    }
  }, []);

  // don't know why the default parameter doesn't work when `null` is passed in
  if (!pageTitle) pageTitle = '';

  const pageDesc = pageDescription || defaultDescription;
  const pageUrl = `${siteUrl}${pathname === '/' ? '' : pathname}`;
  const pageImageUrl = pageImage ? pageImage : `${siteUrl}${defaultLogo}`;

  const getPageTitle = () => {
    switch (titleType) {
      case 'page':
        return pageTitle || defaultTitle;
      case 'site':
        return defaultTitle;
      case 'append':
        return `${pageTitle}${pageTitle ? ` – ${defaultTitle}` : defaultTitle}`;
      case 'prepend':
        return `${pageTitle ? `${defaultTitle} – ` : defaultTitle}${pageTitle}`;
      default:
        return defaultTitle;
    }
  };

  return (
    <>
      <Helmet title={getPageTitle()}>
        <html lang={siteLanguage} />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, user-scalable=0, minimum-scale=1.0, maximum-scale=1.0"
        />
        <link rel="canonical" href={pageUrl} />
        <meta name="description" content={pageDesc} />
        <meta name="image" content={pageImage || defaultLogo} />
      </Helmet>
      {facebook && (
        <Helmet>
          <meta property="fb:app_id" content={facebook} />
          <meta property="og:locale" content={ogLanguage} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={pageTitle || defaultTitle} />
          <meta property="og:description" content={pageDesc} />
          <meta property="og:image" content={pageImageUrl} />
          <meta property="og:image:alt" content={pageDesc} />
        </Helmet>
      )}
      {twitter && (
        <Helmet>
          <meta name="twitter:creator" content={twitter} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle || defaultTitle} />
          <meta name="twitter:description" content={pageDesc} />
          <meta name="twitter:image" content={pageImageUrl} />
          <meta name="twitter:image:alt" content={pageDesc} />
        </Helmet>
      )}
    </>
  );
};

const query = graphql`
  {
    site {
      siteMetadata {
        author
        defaultDescription: description
        defaultLogo: logo
        defaultTitle: title
        facebook
        ogLanguage
        publisherLogo
        siteLanguage
        siteUrl
        twitter
      }
    }
  }
`;

Meta.propTypes = {
  pageDescription: PropTypes.string,
  pageImage: PropTypes.string,
  pageTitle: PropTypes.string,
  pathname: PropTypes.string,
  titleType: PropTypes.string,
};

export default (props) => (
  <StaticQuery
    query={`${query}`}
    render={(data) => <Meta data={data} {...props} />}
  />
);
